import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";

// UUID Generator
const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * useChat - Hook لإدارة المحادثة
 */
export function useChat({ sessionId, t, isRTL }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId || null);
  const [currentAssistant, setCurrentAssistant] = useState(null);
  const inputRef = useRef(null);

  // Load Assistant
  const loadAssistant = useCallback(async (assistantId) => {
    try {
      const response = await api.get(`/assistants/${assistantId}`);
      setCurrentAssistant(response.data);
      if (messages.length === 0) {
        const greeting = isRTL ? response.data.greeting : response.data.greeting_en;
        if (greeting) {
          setMessages([{
            id: uuidv4(),
            role: "assistant",
            content: greeting,
            timestamp: new Date().toISOString()
          }]);
        }
      }
    } catch (err) {
      console.warn('Assistant greeting error:', err);
    }
  }, [messages.length, isRTL]);

  // Fetch History
  const fetchHistory = useCallback(async (sid) => {
    try {
      const response = await api.get(`/chat/history/${sid}`);
      setMessages(response.data);
    } catch (err) {
      console.warn('Fetch history error:', err);
    }
  }, []);

  // Send Text Message
  const sendMessage = useCallback(async (text, sessionIdOverride) => {
    if (!text.trim() || loading) return;

    const sid = sessionIdOverride || currentSessionId || uuidv4();
    if (!currentSessionId) setCurrentSessionId(sid);

    setMessages(prev => [...prev, {
      id: uuidv4(),
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    }]);

    setLoading(true);

    try {
      const endpoint = currentAssistant ? '/assistants/chat' : '/chat';
      const payload = currentAssistant 
        ? { session_id: sid, message: text, assistant_id: currentAssistant.id }
        : { session_id: sid, message: text };

      const response = await api.post(endpoint, payload);

      setMessages(prev => [...prev, {
        id: uuidv4(),
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString(),
        model_used: response.data.model_used
      }]);

      if (!sessionId) {
        navigate(`/chat/${response.data.session_id || sid}`, { replace: true });
      }
      
      return response.data.response;
    } catch {
      toast.error(t("messageSendError"));
      setMessages(prev => prev.slice(0, -1));
      return null;
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [loading, currentSessionId, currentAssistant, sessionId, navigate, t]);

  // Send Image Message
  const sendImageMessage = useCallback(async (imageBase64, imagePreview, text) => {
    setLoading(true);
    
    setMessages(prev => [...prev, {
      id: Date.now(),
      role: 'user',
      content: text || '📷',
      image: imagePreview
    }]);

    try {
      const prompt = text 
        ? `المستخدم أرسل صورة مع السؤال: "${text}". حلل الصورة وأجب.`
        : 'حلل هذه الصورة ووصف محتواها بالتفصيل.';
      
      const response = await api.post('/chat', {
        message: prompt,
        session_id: currentSessionId,
        image_base64: imageBase64
      });
      
      if (response.data) {
        if (!currentSessionId && response.data.session_id) {
          setCurrentSessionId(response.data.session_id);
          navigate(`/chat/${response.data.session_id}`, { replace: true });
        }
        
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          role: 'assistant',
          content: response.data.response
        }]);
        
        return response.data.response;
      }
    } catch {
      toast.error(t("imageAnalysisError"));
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
    return null;
  }, [currentSessionId, navigate, t]);

  // Send File Message
  const sendFileMessage = useCallback(async (fileBase64, filename, text) => {
    setLoading(true);
    
    setMessages(prev => [...prev, {
      id: Date.now(),
      role: 'user',
      content: text ? `${text}\n📎 ${filename}` : `📎 ${filename}`
    }]);

    try {
      const response = await api.post('/analyze-file', {
        file_base64: fileBase64,
        filename: filename,
        question: text || "قم بتلخيص محتوى هذا الملف",
        session_id: currentSessionId
      });
      
      if (response.data) {
        if (!currentSessionId && response.data.session_id) {
          setCurrentSessionId(response.data.session_id);
          navigate(`/chat/${response.data.session_id}`, { replace: true });
        }
        
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          role: 'assistant',
          content: response.data.response
        }]);
        
        return response.data.response;
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t("fileAnalysisError");
      toast.error(errorMsg);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
    return null;
  }, [currentSessionId, navigate, t]);

  // Close Assistant
  const closeAssistant = useCallback(() => {
    setCurrentAssistant(null);
    navigate("/chat", { replace: true });
  }, [navigate]);

  // Reset Session
  const resetSession = useCallback(() => {
    setMessages([]);
    setCurrentSessionId(null);
  }, []);

  return {
    messages,
    setMessages,
    loading,
    currentSessionId,
    setCurrentSessionId,
    currentAssistant,
    inputRef,
    loadAssistant,
    fetchHistory,
    sendMessage,
    sendImageMessage,
    sendFileMessage,
    closeAssistant,
    resetSession
  };
}

export default useChat;
